import React, { useState, useEffect } from 'react';
import { Calendar, IndianRupee, Clock, Shield, Star, Trash2, RefreshCw, Download, Filter, RotateCw } from 'lucide-react';
import { getSubmissions, moveToTrash, restoreFromTrash } from '../db/client';
import SubmissionDetails from './SubmissionDetails';
import { FormData } from '../App';
import { format, isWithinInterval, parse } from 'date-fns';

interface Submission extends FormData {
  id: string;
  created_at: string;
  total_cost: number;
  total_duration: number;
  deleted?: boolean;
  deleted_at?: string;
}

interface FilterState {
  startDate: string;
  endDate: string;
  state: string;
  district: string;
  pattern: string;
}

function SubmissionsList() {
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [deletedSubmissions, setDeletedSubmissions] = useState<Submission[]>([]);
  const [showTrash, setShowTrash] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    startDate: '',
    endDate: '',
    state: '',
    district: '',
    pattern: ''
  });

  const loadSubmissions = async () => {
    setIsLoading(true);
    try {
      const activeSubmissions = await getSubmissions(false);
      const trashedSubmissions = await getSubmissions(true);
      setSubmissions(activeSubmissions);
      setDeletedSubmissions(trashedSubmissions.filter(s => s.deleted));
    } catch (error) {
      console.error('Error loading submissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubmissions();
  }, [showTrash]);

  const filterSubmissions = (data: Submission[]) => {
    return data.filter(submission => {
      const date = new Date(submission.created_at);
      const matchesDateRange = (!filters.startDate || !filters.endDate) || 
        isWithinInterval(date, {
          start: parse(filters.startDate, 'yyyy-MM-dd', new Date()),
          end: parse(filters.endDate, 'yyyy-MM-dd', new Date())
        });
      const matchesState = !filters.state || submission.state === filters.state;
      const matchesDistrict = !filters.district || submission.district === filters.district;
      const matchesPattern = !filters.pattern || submission.croppingPattern === filters.pattern;
      
      return matchesDateRange && matchesState && matchesDistrict && matchesPattern;
    });
  };

  const handleDelete = async (submissionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (window.confirm('Are you sure you want to move this item to trash?')) {
      try {
        await moveToTrash(submissionId);
        await loadSubmissions();
      } catch (error) {
        console.error('Error deleting submission:', error);
        alert('Failed to delete submission. Please try again.');
      }
    }
  };

  const handleRestore = async (submissionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await restoreFromTrash(submissionId);
      await loadSubmissions();
    } catch (error) {
      console.error('Error restoring submission:', error);
      alert('Failed to restore submission. Please try again.');
    }
  };

  const exportToCSV = () => {
    const filteredData = filterSubmissions(showTrash ? deletedSubmissions : submissions);
    
    const baseHeaders = [
      'Date',
      'State',
      'District',
      'Pattern',
      'Total Cost (₹)',
      'Duration (days)',
      'Safety Rating',
      'Efficacy Rating',
      'Herbicide Applications Count',
      'Manual Weeding Operations Count'
    ];

    const maxHerbicides = Math.max(...filteredData.map(item => item.herbicides.length));
    const maxManualWeeding = Math.max(...filteredData.map(item => item.manualWeedingDetails.length));

    const herbicideHeaders = Array.from({ length: maxHerbicides }, (_, i) => [
      `Herbicide ${i + 1} Type`,
      `Herbicide ${i + 1} Products`,
      `Herbicide ${i + 1} Product Costs`,
      `Herbicide ${i + 1} Spray Man Cost`,
      `Herbicide ${i + 1} Control Duration`
    ]).flat();

    const manualWeedingHeaders = Array.from({ length: maxManualWeeding }, (_, i) => [
      `Manual Weeding ${i + 1} Laborers`,
      `Manual Weeding ${i + 1} Cost per Laborer`,
      `Manual Weeding ${i + 1} Control Duration`
    ]).flat();

    const allHeaders = [...baseHeaders, ...herbicideHeaders, ...manualWeedingHeaders];

    const csvContent = [
      allHeaders.join(','),
      ...filteredData.map(item => {
        const baseData = [
          format(new Date(item.created_at), 'yyyy-MM-dd'),
          item.state,
          item.district,
          item.croppingPattern,
          item.total_cost,
          item.total_duration,
          item.cropSafetyRating,
          item.herbicideEfficacyRating,
          item.herbicideApplications,
          item.manualWeeding
        ];

        const herbicideData = Array.from({ length: maxHerbicides }, (_, i) => {
          const herb = item.herbicides[i];
          if (!herb) return ['', '', '', '', ''];
          return [
            herb.type,
            herb.products.map(p => p.productName).join(';'),
            herb.products.map(p => p.cost).join(';'),
            herb.sprayManCost,
            herb.controlDuration
          ];
        }).flat();

        const manualWeedingData = Array.from({ length: maxManualWeeding }, (_, i) => {
          const mw = item.manualWeedingDetails[i];
          if (!mw) return ['', '', ''];
          return [
            mw.laborers,
            mw.costPerLaborer,
            mw.controlDuration
          ];
        }).flat();

        return [...baseData, ...herbicideData, ...manualWeedingData].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `weed-control-detailed-data-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uniqueStates = Array.from(new Set(submissions.map(s => s.state))).filter(Boolean);
  const uniqueDistricts = Array.from(new Set(submissions.map(s => s.district))).filter(Boolean);
  const patterns = ['TPR', 'DSR'];

  return (
    <div className="min-h-screen bg-brand-yellow/90 p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold text-brand-blue">
              {showTrash ? 'Trash' : 'Saved Calculations'}
            </h1>
            <button
              onClick={() => setShowTrash(!showTrash)}
              className="inline-flex items-center px-4 py-2 text-sm bg-white/30 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/40 transition-colors"
            >
              {showTrash ? 'View Active' : 'View Trash'}
            </button>
            <button
              onClick={loadSubmissions}
              className="inline-flex items-center px-4 py-2 text-sm bg-white/30 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/40 transition-colors"
            >
              <RotateCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center px-4 py-2 text-sm bg-white/30 backdrop-blur-sm border border-white/20 rounded-full hover:bg-white/40 transition-colors"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <button
              onClick={exportToCSV}
              className="inline-flex items-center px-4 py-2 text-sm bg-gradient-brand text-white rounded-full shadow-lg shadow-brand-green/20 hover:shadow-brand-green/30 hover:scale-[1.02] transition-all duration-200"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Data
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="backdrop-blur-md bg-white/30 rounded-2xl p-6 mb-6 border border-white/20 grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
              <select
                value={filters.state}
                onChange={(e) => setFilters({ ...filters, state: e.target.value, district: '' })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="">All States</option>
                {uniqueStates.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">District</label>
              <select
                value={filters.district}
                onChange={(e) => setFilters({ ...filters, district: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
                disabled={!filters.state}
              >
                <option value="">All Districts</option>
                {uniqueDistricts.map((district) => (
                  <option key={district} value={district}>{district}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pattern</label>
              <select
                value={filters.pattern}
                onChange={(e) => setFilters({ ...filters, pattern: e.target.value })}
                className="w-full p-2 border border-gray-300 rounded-lg"
              >
                <option value="">All Patterns</option>
                {patterns.map((pattern) => (
                  <option key={pattern} value={pattern}>{pattern}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="backdrop-blur-md bg-white/30 rounded-2xl shadow-xl border border-white/20 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-600">
              <p className="text-lg">Loading...</p>
            </div>
          ) : filterSubmissions(showTrash ? deletedSubmissions : submissions).length === 0 ? (
            <div className="p-8 text-center text-gray-600">
              <p className="text-lg">
                {showTrash ? 'No items in trash.' : 'No calculations found.'}
              </p>
              <p className="mt-2">
                {showTrash ? 'Deleted items will appear here.' : 'Start by creating a new calculation.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-gradient-brand">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">State</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">District</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">Pattern</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">Total Cost</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">Duration</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">Safety</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">Efficacy</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">Date</th>
                    <th className="px-6 py-4 text-left text-sm font-medium text-white">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {filterSubmissions(showTrash ? deletedSubmissions : submissions).map((submission) => (
                    <tr
                      key={submission.id}
                      onClick={() => setSelectedSubmission(submission)}
                      className="hover:bg-white/20 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 text-sm text-gray-800">{submission.state}</td>
                      <td className="px-6 py-4 text-sm text-gray-800">{submission.district}</td>
                      <td className="px-6 py-4 text-sm text-gray-800">{submission.croppingPattern}</td>
                      <td className="px-6 py-4 text-sm text-gray-800">
                        <span className="inline-flex items-center">
                          <IndianRupee className="w-4 h-4 mr-1" />
                          {submission.total_cost.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800">
                        <span className="inline-flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {submission.total_duration} days
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800">
                        <span className="inline-flex items-center">
                          <Shield className="w-4 h-4 mr-1 text-brand-blue" />
                          {submission.cropSafetyRating}/10
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800">
                        <span className="inline-flex items-center">
                          <Star className="w-4 h-4 mr-1 text-brand-orange" />
                          {submission.herbicideEfficacyRating}/10
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800">
                        <span className="inline-flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {format(new Date(submission.created_at), 'dd/MM/yyyy')}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-800">
                        <button
                          onClick={(e) => showTrash ? handleRestore(submission.id, e) : handleDelete(submission.id, e)}
                          className={`p-2 rounded-full transition-colors ${
                            showTrash 
                              ? 'hover:bg-green-100 text-green-600' 
                              : 'hover:bg-red-100 text-red-600'
                          }`}
                        >
                          {showTrash ? <RefreshCw className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedSubmission && (
        <SubmissionDetails 
          submission={selectedSubmission} 
          onClose={() => setSelectedSubmission(null)} 
        />
      )}
    </div>
  );
}

export default SubmissionsList;